import React, { memo, useState } from "react";
import {
  Card,
  Chip,
  CardContent,
  Typography,
  Box,
  Divider,
  Stack,
  IconButton,
  Collapse,
  Grid,
  CardHeader,
  useTheme,
  Tooltip,
} from "@mui/material";
import { red, amber } from "@mui/material/colors";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import EventIcon from "@mui/icons-material/Event";
import { useNavigate } from "react-router-dom";
import { WorkOrder } from "../models/workOrderTypes";
import {
  capitalizeFirstLetter,
  truncateName,
} from "../../common/Utils/commonUtilityFunctions";
import { useSelector } from "react-redux";
import "./workOrderCardDetails.css";

import WorkOrderDialogContainer from "components/workOrder/WorkOrderDialogContainerMain";
import { RootState } from "../../../app";
import {
  customerName,
  timeOnChip,
  woCardAction,
  woActionIconButtonSx,
  woActionIconSx,
  woActionIconFontSize,
  woCalendarIconSx,
} from "../models/workOrderStyles";
import { AccessTime } from "@mui/icons-material";
// import CustomerProfileImage from "./CustomerProfileImage";
import WorkOrderCardBody from "./WorkOrderCardBody";
import WorkOrderCardDateDaysStatusRow from "./WorkOrderCardDateDaysStatusRow";
import WorkOrderCardDetailField from "./WorkOrderCardDetailField";
import { BoardView } from "../types/boardView";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { User } from "components/authentication/models/authTypes";
import packageJson from "../../../../package.json";
import localStorageHelper from "../../../app/localStorage";
// import CustomerDetailDialog from "components/workOrder/customer/CustomerDetailDialog";
// import { CustomerAPI } from "components/QueueBoard/api/customerApi";
import WorkOrderOpenIconButtons from "components/common/WorkOrderOpen/WorkOrderOpenIconButtons";
const WorkOrderCard = memo(
  ({
    workOrderObj,
    recentlyDroppedWorkOrder,
    isCollapsed = false,
    boardView,
  }: {
    index: number;
    color: string;
    workOrderObj: WorkOrder;
    recentlyDroppedWorkOrder?: number | null;
    isCollapsed?: boolean;
    boardView?: BoardView;
  }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [user] = useState<User | undefined>(
      localStorageHelper.getByKey("user"),
    );
    const [open, setOpen] = useState(false);
    const [invDocId, setInvDocId] = useState<number>(0);
    const resolvedBoardView = boardView ?? BoardView.Status;
    const { filters } = useSelector(
      (state: RootState) => state.workOrderDashboard,
    );
    const [showDialog, setShowDialog] = useState(false);

    // Check if this work order should show the flashing effect
    const isRecentlyDropped =
      recentlyDroppedWorkOrder === workOrderObj.invoiceDocumentIdentity;

    // // Debug logging
    // if (recentlyDroppedWorkOrder !== null) {
    //   console.log("WorkOrderCard Debug:", {
    //     workOrderId: workOrderObj.invoiceDocumentIdentity,
    //     workOrderIdType: typeof workOrderObj.invoiceDocumentIdentity,
    //     recentlyDroppedWorkOrder,
    //     recentlyDroppedWorkOrderType: typeof recentlyDroppedWorkOrder,
    //     isRecentlyDropped,
    //     comparison:
    //       recentlyDroppedWorkOrder === workOrderObj.invoiceDocumentIdentity,
    //   });
    // }

    const imgCommentDialogHandler = (InvoiceDocId: number) => {
      openWorkOrderInServiceQueue(InvoiceDocId, 1);

      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        "ImageCommentButtonClicked",
        {
          elapsed_ms: "124",
          title: "Work Order Details",
          componentName: "WorkOrderDetails.tsx",
          version: packageJson.version,
        },
      );
    };
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const openWorkOrderInServiceQueue = (
      workOrderIdentity: number,
      tabIndex: number,
    ) => {
      setShowDialog(true);
      setCurrentTabIndex(tabIndex);
      setInvDocId(workOrderIdentity);
    };
    const showHideWorkOrderPopup = (isPopupToShow: boolean) => {
      setShowDialog(isPopupToShow);
      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        (isPopupToShow ? "open " : "close ") + "workOrderDetails",
        {
          elapsed_ms: "124",
          title: "Work Order Details",
          componentName: "WorkOrderDetails.tsx",
          version: packageJson.version,
        },
      );
    };
    // Customer avatar / contact detail dialog temporarily disabled
    // const [customerDetailDialogOpen, setCustomerDetailDialogOpen] =
    //   useState(false);
    // const [customerDetails, setCustomerDetails] = useState<{
    //   customerPhone?: string | null;
    //   customerEmail?: string | null;
    //   customerAddress?: string | null;
    // }>({});

    const myReferenceText = workOrderObj.woDisplayText?.trim() || "";
    const hasMyReference = myReferenceText.length > 0;
    const descriptionText = workOrderObj.description?.trim() || "";

    // Toggle function for the Collapse
    const handleToggleCollapse = () => {
      setOpen(!open);
    };

    // const handleCustomerAvatarClick = async () => {
    //   if (!workOrderObj.customerIdentity) return;
    //   const customer = await CustomerAPI.getCustomerContactDetailsById(
    //     Number(workOrderObj.customerIdentity),
    //   );
    //   if (customer) {
    //     setCustomerDetails({
    //       customerPhone: customer.customerPhone,
    //       customerEmail: customer.customerEmail,
    //       customerAddress: customer.customerAddress,
    //     });
    //     setCustomerDetailDialogOpen(true);
    //   }
    // };
    return (
      <React.Fragment>
        <Card
          className="woCardDetails"
          sx={{
            mb: 1,
            p: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            transition: "all 0.3s ease",
            backgroundColor: theme.palette.background.paper,
            "&:hover": {
              boxShadow: 6,
              borderColor: theme.palette.primary.main,
              transform: "translateY(-2px)",
            },
            // Add flashing animation directly in sx prop
            ...(isRecentlyDropped && {
              animation: "flash 3s ease-in-out",
              "@keyframes flash": {
                "0%": {
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: `1px solid ${theme.palette.divider}`,
                  transform: "scale(1)",
                },
                "10%": {
                  backgroundColor: theme.palette.primary.light,
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                  border: `2px solid ${theme.palette.primary.main}`,
                  transform: "scale(1.02)",
                },
                "20%": {
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: `1px solid ${theme.palette.divider}`,
                  transform: "scale(1)",
                },
                "30%": {
                  backgroundColor: theme.palette.primary.light,
                  boxShadow: `0 8px 25px ${theme.palette.primary.main}70`,
                  border: `3px solid ${theme.palette.primary.main}`,
                  transform: "scale(1.03)",
                },
                "40%": {
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: `1px solid ${theme.palette.divider}`,
                  transform: "scale(1)",
                },
                "50%": {
                  backgroundColor: theme.palette.primary.light,
                  boxShadow: `0 10px 30px ${theme.palette.primary.main}80`,
                  border: `2px solid ${theme.palette.primary.main}`,
                  transform: "scale(1.02)",
                },
                "60%": {
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: `1px solid ${theme.palette.divider}`,
                  transform: "scale(1)",
                },
                "70%": {
                  backgroundColor: theme.palette.primary.light,
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                  border: `2px solid ${theme.palette.primary.main}`,
                  transform: "scale(1.01)",
                },
                "80%": {
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: `1px solid ${theme.palette.divider}`,
                  transform: "scale(1)",
                },
                "90%": {
                  backgroundColor: theme.palette.primary.light,
                  boxShadow: `0 4px 15px ${theme.palette.primary.main}50`,
                  border: `2px solid ${theme.palette.primary.main}`,
                  transform: "scale(1.01)",
                },
                "100%": {
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: `1px solid ${theme.palette.divider}`,
                  transform: "scale(1)",
                },
              },
            }),
          }}
        >
          <CardHeader
            sx={{
              pb: "5px",
              pt: "7px",
              "& .MuiCardHeader-action": {
                mr: 0.5,
                alignSelf: "flex-start",
              },
            }}
            action={
              <Box sx={{ ...woCardAction, cursor: "pointer" }}>
                {/* Intake Calendar Icon */}
                {workOrderObj.scheduleItemId && (
                  <Tooltip title="From Service Calendar V2" arrow placement="top">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        size="small"
                        disableRipple
                        tabIndex={-1}
                        sx={{
                          ...woActionIconButtonSx,
                          cursor: "default",
                          "&:hover": { bgcolor: "transparent" },
                        }}
                        onClick={() => {
                          navigate(`/TechnicianCalendar`);
                        }}
                      >
                        <EventIcon sx={woCalendarIconSx} />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}

                {/* Attachment Icon */}
                {workOrderObj.isAttachmentExist && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        imgCommentDialogHandler(
                          workOrderObj.invoiceDocumentIdentity,
                        );
                      }}
                      sx={{
                        ...woActionIconButtonSx,
                        color: "#2371E7",
                        cursor: "pointer",
                      }}
                    >
                      <InsertPhotoOutlinedIcon sx={woActionIconSx} />
                    </IconButton>
                  </Box>
                )}

                {/* Comment Icon Icon */}
                {workOrderObj.isCommentExist && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        imgCommentDialogHandler(
                          workOrderObj.invoiceDocumentIdentity,
                        );
                      }}
                      sx={{ ...woActionIconButtonSx, color: "#2371E7" }}
                    >
                      <MessageOutlinedIcon
                        sx={{
                          ...woActionIconSx,
                          color: "#2371E7",
                          cursor: "pointer",
                        }}
                      />
                    </IconButton>
                  </Box>
                )}

                {/* Open WO */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <WorkOrderOpenIconButtons
                    invoiceDocId={workOrderObj.invoiceDocumentIdentity}
                    onOpenInServiceQueue={(invoiceDocId) =>
                      openWorkOrderInServiceQueue(invoiceDocId, 0)
                    }
                    iconFontSize={woActionIconFontSize}
                    iconButtonSx={{ ...woActionIconButtonSx, color: "#2371E7" }}
                  />
                </Box>
              </Box>
            }
            title={
              <Box
                sx={{ mt: "5px", cursor: "pointer" }}
                onClick={() =>
                  openWorkOrderInServiceQueue(
                    workOrderObj.invoiceDocumentIdentity,
                    0,
                  )
                }
                width="100%"
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        component="div"
                        sx={{
                          fontWeight: "500",
                          fontStyle: "normal",
                          fontSize: "17px",
                          color: theme.palette.text.primary,
                          mr: "3px",
                        }}
                      >
                        {workOrderObj.isTimeOn ? (
                          <Chip sx={{ ...timeOnChip, marginRight: "7px" }} />
                        ) : null}
                        {workOrderObj.invoiceNumber}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={capitalizeFirstLetter(
                          workOrderObj.invoiceStatus,
                        )}
                        size="small"
                        sx={{
                          borderRadius: "5px",
                          color: "white",
                          backgroundColor: "gray",
                          fontWeight: "600",
                          fontSize: "11px",
                          fontStyle: "normal",
                          height: "18px",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                {/* Unit Number */}
              </Box>
            }
            titleTypographyProps={{ component: "div" }}
          />
          <CardContent
            className="woContent"
            sx={{
              padding: "0px",
              backgroundColor: theme.palette.background.paper,
              "&:last-child": {
                paddingBottom: "8px",
              },
              display: isCollapsed ? "none" : "block",
            }}
          >
            {/* Main Content (no onClick here) */}
            <Box>
              <Box
                sx={{
                  ml: "15px",
                  mt: 0.5,
                  mb: 1,
                  pb: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  sx={{
                    ...customerName,
                    color: theme.palette.text.primary,
                  }}
                  variant="subtitle1"
                >
                  {truncateName(workOrderObj.customerName, 25)}
                </Typography>
              </Box>
              <Stack spacing={0.5} sx={{ ml: "15px" }}>
                {hasMyReference && (
                  <WorkOrderCardDetailField
                    label="My Reference:"
                    value={
                      myReferenceText.length > 25
                        ? `${myReferenceText.slice(0, 25)}...`
                        : myReferenceText
                    }
                  />
                )}

                {descriptionText && (
                  <WorkOrderCardDetailField
                    label="Description:"
                    value={descriptionText}
                    sx={{
                      maxWidth: "100%",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                    }}
                  />
                )}

                {workOrderObj.woPriority && (
                  <WorkOrderCardDetailField
                    label="WO Priority:"
                    value={workOrderObj.woPriority}
                  />
                )}

                {workOrderObj.model && (
                  <WorkOrderCardDetailField
                    label="Unit Model:"
                    value={workOrderObj.model}
                  />
                )}

                {workOrderObj.woUnitBaseSerial && (
                  <WorkOrderCardDetailField
                    label="VIN #:"
                    value={
                      workOrderObj.woUnitBaseSerial.length > 35
                        ? `${workOrderObj.woUnitBaseSerial.slice(0, 35)}...`
                        : workOrderObj.woUnitBaseSerial
                    }
                  />
                )}

                {workOrderObj.woPickupDate && (
                  <WorkOrderCardDetailField
                    label="Pickup Date:"
                    value={new Date(
                      workOrderObj.woPickupDate,
                    ).toLocaleDateString()}
                  />
                )}

                {workOrderObj.woDeliveryDate && (
                  <WorkOrderCardDetailField
                    label="Delivery Date:"
                    value={new Date(
                      workOrderObj.woDeliveryDate,
                    ).toLocaleDateString()}
                  />
                )}
              </Stack>

              {/* Single full-width separator after detail fields */}
              <Divider
                sx={{
                  width: "100%",
                  mt: "4px",
                  mb: "2px",
                  borderColor: "divider",
                }}
              />

              {(() => {
                // Determine border color based on elapsed vs quoted hours
                const isExceeded =
                  workOrderObj.quotedHours > 0 &&
                  workOrderObj.elapsedHours >= workOrderObj.quotedHours;
                const isApproaching =
                  workOrderObj.quotedHours > 0 &&
                  workOrderObj.elapsedHours >= workOrderObj.quotedHours * 0.8;
                const hasUnquotedTime =
                  workOrderObj.quotedHours === 0 &&
                  workOrderObj.elapsedHours > 0;
                const showBorder =
                  isExceeded || isApproaching || hasUnquotedTime;
                const borderColor =
                  isExceeded || hasUnquotedTime
                    ? red[500]
                    : isApproaching
                      ? amber[500]
                      : "transparent";

                return (
                  <>
                    <Stack
                      onClick={handleToggleCollapse}
                      sx={{
                        transition: "border-color 0.3s ease",
                        bgcolor:
                          theme.palette.mode === "light"
                            ? "rgba(0, 0, 0, 0.02)"
                            : "rgba(255, 255, 255, 0.05)",
                        width: "100%",
                        cursor: "pointer",
                        borderLeft: showBorder
                          ? `4px solid ${borderColor}`
                          : "none",
                        borderTopLeftRadius: showBorder ? "4px" : "0px",
                        borderBottomLeftRadius: showBorder ? "4px" : "0px",
                        paddingLeft: showBorder ? "10px" : "14px",
                        paddingRight: "12px",
                        "&:hover": {
                          boxShadow: 6,
                          backgroundColor:
                            theme.palette.mode === "light"
                              ? "rgba(0, 0, 0, 0.04)"
                              : "rgba(255, 255, 255, 0.08)",
                        },
                      }}
                      p={1}
                      display="flex"
                    >
                      <Grid container alignItems="center">
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          lg={6}
                          sx={{ minWidth: 0, overflow: "hidden" }}
                        >
                          <WorkOrderCardBody
                            label="Quoted"
                            value={`${workOrderObj.quotedHours.toFixed(2)} Hrs`}
                            icon={
                              <AccessTime
                                fontSize="small"
                                sx={{
                                  ml: "10px",
                                  mr: 1,
                                  color: "text.secondary",
                                  flexShrink: 0,
                                }}
                              />
                            }
                          />
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          md={6}
                          lg={6}
                          sx={{ minWidth: 0, overflow: "hidden" }}
                        >
                          <WorkOrderCardBody
                            label="Clocked"
                            value={`${workOrderObj.elapsedHours.toFixed(2)} Hrs`}
                            icon={
                              <AccessTime
                                fontSize="small"
                                sx={{
                                  mr: 1,
                                  color: "text.secondary",
                                  flexShrink: 0,
                                }}
                              />
                            }
                            highlightColor={
                              (workOrderObj.quotedHours > 0 &&
                                workOrderObj.elapsedHours >=
                                  workOrderObj.quotedHours) ||
                              (workOrderObj.quotedHours === 0 &&
                                workOrderObj.elapsedHours > 0)
                                ? red[500]
                                : workOrderObj.quotedHours > 0 &&
                                    workOrderObj.elapsedHours >=
                                      workOrderObj.quotedHours * 0.8
                                  ? amber[700]
                                  : undefined
                            }
                          />
                        </Grid>
                        {workOrderObj.daysSinceLastClockIn > 0 && (
                          <Grid
                            container
                            mb={1}
                            mt={0.5}
                            sx={{ width: "100%" }}
                          >
                            <Grid
                              item
                              xs={12}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                ml: "25px",
                                minWidth: 0,
                              }}
                            >
                              <WorkOrderCardDetailField
                                label="Last clocking:"
                                value={`${workOrderObj.daysSinceLastClockIn} days ago`}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                    </Stack>

                    <Box
                      sx={{
                        mt: "2px",
                        mb: "5px",
                        width: "calc(100% + 24px)",
                        ml: "-12px",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        borderLeft: showBorder
                          ? `4px solid ${borderColor}`
                          : "none",
                        borderTopLeftRadius: showBorder ? "4px" : "0px",
                        pl: showBorder ? "10px" : "0px",
                      }}
                    />
                  </>
                );
              })()}

              <Collapse
                in={open}
                timeout="auto"
                unmountOnExit
                sx={{ width: "100%", marginLeft: "15px" }}
              >
                <Stack
                  spacing={0.5}
                  onClick={handleToggleCollapse}
                  sx={{
                    alignItems: "flex-start",
                    padding: "0px 2px",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  <WorkOrderCardDetailField
                    label="Unit #:"
                    value={workOrderObj.stockNumber}
                  />

                  <WorkOrderCardDetailField
                    label="Unit Description:"
                    value={workOrderObj.unitDescription}
                    sx={{
                      maxWidth: "100%",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      pr: "15px",
                    }}
                  />

                  <WorkOrderCardDetailField
                    label="WO Priority:"
                    value={workOrderObj.woPriority}
                  />

                  {filters.view === 0 && (
                    <WorkOrderCardDetailField
                      label="Technician:"
                      value={workOrderObj.technicianName}
                    />
                  )}
                </Stack>
                <Divider />
              </Collapse>
            </Box>

            {/* Last row: Wo creation date, days, status */}
            <WorkOrderCardDateDaysStatusRow
              workOrderObj={workOrderObj}
              boardView={resolvedBoardView}
              onToggleExpand={() => setOpen(!open)}
            />
          </CardContent>
        </Card>
        {showDialog && (
          <WorkOrderDialogContainer
            tabIndex={currentTabIndex}
            InvoiceDocIdentity={invDocId}
            showHideWoPopup={showHideWorkOrderPopup}
            open={true}
            cacheNewWorkOrderIdOnSave={
              resolvedBoardView === BoardView.Status ||
              resolvedBoardView === BoardView.Technician ||
              resolvedBoardView === BoardView.Priority
            }
          />
        )}
        {/* Customer Detail Dialog temporarily disabled with avatar
        <CustomerDetailDialog
          open={customerDetailDialogOpen}
          onClose={() => setCustomerDetailDialogOpen(false)}
          customerPhone={customerDetails.customerPhone}
          customerEmail={customerDetails.customerEmail}
          customerAddress={customerDetails.customerAddress}
        />
        */}
      </React.Fragment>
    );
  },
);

export default WorkOrderCard;
